import type { Exercice } from '@mairistem/comptabilite';
import { Icon, type UserRole } from '@mairistem/finances-utils';
import type React from 'react';
import type TreeNodeType from '../../constants/liquidation/TreeNodeType';
import type { TreeRow } from '../../interfaces/pieces/TreeRow';
import TreeDataManager from './TreeDataManager';

abstract class BaseTreeView {
	abstract loadData(rowData: TreeRow): Promise<TreeRow[]>;

	abstract getRowHeight(rowData: TreeRow, expanded: boolean): number;

	abstract renderRow(
		rowData: TreeRow,
		expanded: boolean,
		userRole: UserRole,
		onClick: VoidFunction,
		onDoubleClick: VoidFunction,
		onChange: (
			row: TreeRow,
			shouldRefreshChildren?: boolean,
			newParent?: TreeRow,
		) => void,
		onDelete?: (row: TreeRow) => void,
		onAdd?: (row: TreeRow) => void,
		options?: { filterSens?: string; data?: TreeRow[] },
	): React.JSX.Element;

	abstract orderData(data: TreeRow[], nodeType: TreeNodeType): TreeRow[];

	getExercice = (rowData: TreeRow, data?: TreeRow[]): Exercice => {
		if (rowData?.exercice) {
			return rowData.exercice;
		}

		// Si on a accès aux données sources, on remonte via TreeDataManager
		if (data) {
			let currentRowKey = rowData.rowKey;
			let parent = TreeDataManager.getParentFromData(data, currentRowKey);

			while (parent) {
				if (parent.exercice) {
					return parent.exercice as Exercice;
				}
				currentRowKey = parent.rowKey;
				parent = TreeDataManager.getParentFromData(data, currentRowKey);
			}
		}

		return null;
	};

	abstract isRowExpandable(rowData: TreeRow): boolean;

	handleAddRow = (parentRow: TreeRow, newRow: TreeRow): TreeRow => ({
		...parentRow,
		children: [...(parentRow?.children ?? []), newRow],
	});

	renderTreeToggle = (
		isExpanded: boolean,
		loading: boolean,
	): React.JSX.Element => (
		<Icon
			color="blue"
			// TODO : revoir
			data-testid="treeToggle-expand-icon"
			size="large"
			loading={loading}
			name={
				loading ? 'circle notch' : `chevron ${isExpanded ? 'down' : 'right'}`
			}
		/>
	);
}

export default BaseTreeView;
